import React, { useState, useEffect } from "react";
import { collection, addDoc, getDocs, serverTimestamp } from "firebase/firestore";
import { db, storage } from './../../firebase/firebaseConfig'; // Adjust the import if needed
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import Layout from "./Layout";
import PageTitle from "../includes/PageTitle";
import './../css/AdminForms.css';

const AddProduct = () => {
  const [formData, setFormData] = useState({
    name: "",
    price: "",
    sizes: [],
    colors: [],
    productDetails: "",
    category: "",
    subCategory: "",
    gender: "",
    collection: "",
    season: "",
    images: [],
    modelName: "",
    modelHeight: "",
    chest: "",
    waist: "",
    materials: [""],
    quantity: 1,
    type: "",
    sold: 0, // Initialize the sold field
  });

  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [genders, setGenders] = useState([]);
  const [collections, setCollections] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [allSizes, setAllSizes] = useState([]);
  const [allColors, setAllColors] = useState([]);

  // Fetch data from Firestore
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [
          categoriesSnapshot, 
          subCategoriesSnapshot, 
          gendersSnapshot, 
          collectionsSnapshot, 
          seasonsSnapshot, 
          sizesSnapshot, 
          colorsSnapshot
        ] = await Promise.all([
          getDocs(collection(db, "Categories")),
          getDocs(collection(db, "sub_category")),
          getDocs(collection(db, "gender")),
          getDocs(collection(db, "Collections")),
          getDocs(collection(db, "Seasons")),
          getDocs(collection(db, "Sizes")),
          getDocs(collection(db, "Colors"))
        ]);

        const fetchedCategories = categoriesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        const fetchedSubCategories = subCategoriesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        const fetchedGenders = gendersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        const fetchedCollections = collectionsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        const fetchedSeasons = seasonsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        const fetchedSizes = sizesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        const fetchedColors = colorsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

        setCategories(fetchedCategories);
        setSubCategories(fetchedSubCategories);
        setGenders(fetchedGenders);
        setCollections(fetchedCollections);
        setSeasons(fetchedSeasons);
        setAllSizes(fetchedSizes);
        setAllColors(fetchedColors);
      } catch (error) {
        console.error("Error fetching data: ", error);
      }
    };

    fetchData();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({ ...prevState, [name]: value }));
  };

  const handleCheckboxChange = (e, field) => {
    const { value, checked } = e.target;
    setFormData(prevState => {
      const newValue = checked 
        ? [...prevState[field], value] 
        : prevState[field].filter(item => item !== value);
      return { ...prevState, [field]: newValue };
    });
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    setFormData(prevState => ({ ...prevState, images: files }));
  };

  const handleMaterialChange = (e, index) => {
    const newMaterials = [...formData.materials];
    newMaterials[index] = e.target.value;
    setFormData(prevState => ({ ...prevState, materials: newMaterials }));
  };

  const addMaterial = () => {
    setFormData(prevState => ({ ...prevState, materials: [...prevState.materials, ""] }));
  };

  const removeMaterial = (index) => {
    setFormData(prevState => ({ 
      ...prevState, 
      materials: prevState.materials.filter((_, i) => i !== index) 
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const imageUrls = await Promise.all(
        formData.images.map(async (img) => {
          const imageRef = ref(storage, `images/${img.name}`);
          await uploadBytes(imageRef, img);
          const url = await getDownloadURL(imageRef);
          return url;
        })
      );

      const newProduct = {
        ...formData,
        images: imageUrls,
        createdAt: serverTimestamp() // Add the timestamp here
      };

      await addDoc(collection(db, "Products"), newProduct);
      alert("Product added successfully!");
      setFormData({
        name: "",
        price: "",
        sizes: [],
        colors: [],
        productDetails: "",
        category: "",
        subCategory: "",
        gender: "",
        collection: "",
        season: "",
        images: [],
        modelName: "",
        modelHeight: "",
        chest: "",
        waist: "",
        materials: [""],
        quantity: 1,
        type: "",
        sold: 0, // Reset the sold field
      });
    } catch (error) {
      console.error("Error adding product: ", error);
      alert("Error adding product.");
    }
  };

  // Filter subCategories based on selected category
  const filteredSubCategories = subCategories.filter(subCat => subCat.category === formData.category);

  return (
    <>
      <PageTitle title="Add Product - ALMOSTINVISIBLE" />
      <Layout>
        <div className="mt-5">
          <h1 className="unbounded">Add Product</h1>
          <hr />
          <form onSubmit={handleSubmit} className="form-admin">
            {/* Product Name */}
            <div className="form-group">
              <label>Name*</label>
              <input
                type="text"
                className="inputForm"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                placeholder="Enter product name"
              />
            </div>

            {/* Price */}
            <div className="form-group">
              <label>Price*</label>
              <input
                type="number"
                className="inputForm"
                name="price"
                value={formData.price}
                onChange={handleChange}
                required
                placeholder="Enter product price"
              />
            </div>

            {/* Sizes */}
            <div className="form-group">
              <label>Sizes*</label>
              <div>
                {allSizes.map(size => (
                  <div key={size.id} className="form-check form-check-inline">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      name="sizes"
                      value={size.name}
                      checked={formData.sizes.includes(size.name)}
                      onChange={(e) => handleCheckboxChange(e, "sizes")}
                    />
                    <label className="form-check-label">{size.name}</label>
                  </div>
                ))}
              </div>
            </div>
            <br />

            {/* Colors */}
            <div className="form-group">
              <label>Colors*</label>
              <div>
                {allColors.map(color => (
                  <div key={color.id} className="form-check form-check-inline">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      name="colors"
                      value={color.name}
                      checked={formData.colors.includes(color.name)}
                      onChange={(e) => handleCheckboxChange(e, "colors")}
                    />
                    <span
                      className="color-circle"
                      style={{ backgroundColor: color.color }}
                    ></span>
                    <label className="form-check-label">{color.name}</label>
                  </div>
                ))}
              </div>
            </div>
            <br />

            {/* Product Details */}
            <div className="form-group">
              <label>Product Details*</label>
              <textarea
                className="inputForm"
                name="productDetails"
                value={formData.productDetails}
                onChange={handleChange}
                required
                placeholder="Enter product details"
                rows="50"
                style={{resize: "none", height: "40vh", padding: "2vh 2vw"}}
              />
            </div>

            {/* Category */}
            <div className="form-group">
              <label>Category*</label>
              <select
                name="category"
                value={formData.category}
                onChange={handleChange}
                className="inputForm"
                required
              >
                <option value="">Select Category</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
            </div>

            {/* Sub-Category */}
            <div className="form-group">
              <label>Sub-Category*</label>
              <select
                name="subCategory"
                value={formData.subCategory}
                onChange={handleChange}
                className="inputForm"
                required
              >
                <option value="">Select Sub-Category</option>
                {filteredSubCategories.map(subCat => (
                  <option key={subCat.id} value={subCat.id}>{subCat.sub_category_name}</option>
                ))}
              </select>
            </div>

            {/* Gender */}
            <div className="form-group">
              <label>Gender*</label>
              <select
                name="gender"
                value={formData.gender}
                onChange={handleChange}
                className="inputForm"
                required
              >
                <option value="">Select Gender</option>
                {genders.map(gen => (
                  <option key={gen.id} value={gen.id}>{gen.gender_name}</option>
                ))}
              </select>
            </div>

            {/* Collection */}
            <div className="form-group">
              <label>Collection*</label>
              <select
                name="collection"
                value={formData.collection}
                onChange={handleChange}
                className="inputForm"
                required
              >
                <option value="">Select Collection</option>
                {collections.map(col => (
                  <option key={col.id} value={col.id}>{col.collection_name}</option>
                ))}
              </select>
            </div>

            {/* Season */}
            <div className="form-group">
              <label>Season*</label>
              <select
                name="season"
                value={formData.season}
                onChange={handleChange}
                className="inputForm"
                required
              >
                <option value="">Select Season</option>
                {seasons.map(season => (
                  <option key={season.id} value={season.id}>{season.season_name}</option>
                ))}
              </select>
            </div>

            {/* Images */}
            <div className="form-group">
              <label>Images*</label>
              <input
                type="file"
                multiple
                className="inputForm"
                onChange={handleImageChange}
                required
              />
            </div>

            {/* Quantity */}
            <div className="form-group">
              <label>Quantity*</label>
              <input
                type="number"
                className="inputForm"
                name="quantity"
                value={formData.quantity}
                onChange={handleChange}
                required
                placeholder="Enter product quantity"
              />
            </div>

            {/* Type */}
            <div className="form-group">
              <label>Type*</label>
              <select
                name="type"
                value={formData.type}
                onChange={handleChange}
                className="inputForm"
                required
              >
                <option value="">Select Type</option>
                <option value="buy_now">Buy Now</option>
                <option value="pre_order">Pre Order</option>
              </select>
            </div>


            {/* Model Information */}
            <div className="form-group">
              <label>Model Name*</label>
              <input
                type="text"
                name="modelName"
                value={formData.modelName}
                onChange={handleChange}
                className="inputForm"
                required
                placeholder="Enter model name"
              />
            </div>

            <div className="form-group">
              <label>Model Height*</label>
              <input
                type="number"
                name="modelHeight"
                value={formData.modelHeight}
                onChange={handleChange}
                className="inputForm"
                required
                placeholder="Enter model height"
              />
            </div>

            <div className="form-group">
              <label>Chest*</label>
              <input
                type="number"
                name="chest"
                value={formData.chest}
                onChange={handleChange}
                className="inputForm"
                required
                placeholder="Enter chest measurement"
              />
            </div>

            <div className="form-group">
              <label>Waist*</label>
              <input
                type="number"
                name="waist"
                value={formData.waist}
                onChange={handleChange}
                className="inputForm"
                required
                placeholder="Enter waist measurement"
              />
            </div>

            {/* Materials */}
            <div className="form-group ">
              <label>Materials*</label>
              {formData.materials.map((material, index) => (
                <div key={index} className="material-item d-flex mt-2">
                  <input
                    type="text"
                    value={material}
                    onChange={(e) => handleMaterialChange(e, index)}
                    className="inputForm"
                    placeholder="Enter material"
                    required
                  />
                  <div className="d-flex justify-content-between">
                    <button type="button" onClick={() => removeMaterial(index)} className="red-button mx-2">
                      <svg stroke="currentColor" fill="currentColor" stroke-width="0" viewBox="0 0 16 16" height="1.2em" width="1.2em" xmlns="http://www.w3.org/2000/svg"><path d="M5.5 5.5A.5.5 0 016 6v6a.5.5 0 01-1 0V6a.5.5 0 01.5-.5zm2.5 0a.5.5 0 01.5.5v6a.5.5 0 01-1 0V6a.5.5 0 01.5-.5zm3 .5a.5.5 0 00-1 0v6a.5.5 0 001 0V6z"></path><path fill-rule="evenodd" d="M14.5 3a1 1 0 01-1 1H13v9a2 2 0 01-2 2H5a2 2 0 01-2-2V4h-.5a1 1 0 01-1-1V2a1 1 0 011-1H6a1 1 0 011-1h2a1 1 0 011 1h3.5a1 1 0 011 1v1zM4.118 4L4 4.059V13a1 1 0 001 1h6a1 1 0 001-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z" clip-rule="evenodd"></path></svg>
                    </button>
                    <button type="button" onClick={addMaterial} className="black-button mx-2">
                      <svg stroke="currentColor" fill="currentColor" stroke-width="0" t="1551322312294" viewBox="0 0 1024 1024" version="1.1" pId="10297" height="1.2em" width="1.2em" xmlns="http://www.w3.org/2000/svg"><defs></defs><path d="M474 152m8 0l60 0q8 0 8 8l0 704q0 8-8 8l-60 0q-8 0-8-8l0-704q0-8 8-8Z" pId="10298"></path><path d="M168 474m8 0l672 0q8 0 8 8l0 60q0 8-8 8l-672 0q-8 0-8-8l0-60q0-8 8-8Z" pId="10299"></path></svg>
                    </button>
                  </div>
                  
                </div>
              ))}
              
            </div>

            
            

            <button type="submit" className="black-button">Add Product</button>
          </form>
        </div>
      </Layout>
    </>
  );
};

export default AddProduct;
